/**
 * DevelopmentGoalsTab Component
 * Manages recommended development plans AND recommended resources per competency
 */

import React, { useState } from "react";
import {
  useGetDevelopmentGoalsQuery,
  useAddDevelopmentGoalMutation,
  useUpdateDevelopmentGoalMutation,
  useDeleteDevelopmentGoalMutation,
  useGetRecommendedResourcesQuery,
  useAddRecommendedResourceMutation,
  useRemoveRecommendedResourceMutation,
} from "../../../services/api";
import LearningPickerModal from "./LearningPickerModal";

// Icons
const IconChevron = ({ isExpanded }) => (
  <svg
    width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const GOAL_TYPE_OPTIONS = [
  { value: 'action', label: 'Action', color: '#3b82f6' },
  { value: 'skill', label: 'Skill', color: '#10b981' },
  { value: 'attitude', label: 'Attitude', color: '#8b5cf6' },
];

/**
 * GoalFormModal - Form for adding/editing recommended plans (no resources)
 */
function GoalFormModal({ isOpen, onClose, onSave, goal, competencyInfo, isSaving }) {
  const [formData, setFormData] = useState({
    goalDescription: goal?.goalDescription || "",
    goalTypes: goal?.goalTypes || [],
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.goalTypes.length === 0) {
      alert('Please select at least one goal type');
      return;
    }
    onSave(formData);
  };

  const handleToggleType = (type) => {
    setFormData((prev) => ({
      ...prev,
      goalTypes: prev.goalTypes.includes(type)
        ? prev.goalTypes.filter((t) => t !== type)
        : [...prev.goalTypes, type],
    }));
  };

  return (
    <div className="dgt-modal-overlay" onClick={onClose}>
      <div className="dgt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dgt-modal-header">
          <h2>{goal ? "Edit Recommendation" : "Add Recommended Plan"}</h2>
          <button className="dgt-modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="dgt-modal-body">
            {competencyInfo && (
              <div className="dgt-modal-context">
                <span className="dgt-context-value">{competencyInfo.valueName}</span>
                <span className="dgt-context-arrow">&rarr;</span>
                <span className="dgt-context-competency">{competencyInfo.competencyName}</span>
              </div>
            )}

            <div className="dgt-form-group">
              <label>Goal Type *</label>
              <p className="dgt-form-hint">Select one or more types that describe this goal</p>
              <div className="dgt-goal-types">
                {GOAL_TYPE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={`dgt-type-checkbox ${formData.goalTypes.includes(option.value) ? 'selected' : ''}`}
                    style={{
                      '--type-color': option.color,
                      borderColor: formData.goalTypes.includes(option.value) ? option.color : '#d1d5db',
                      backgroundColor: formData.goalTypes.includes(option.value) ? `${option.color}15` : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.goalTypes.includes(option.value)}
                      onChange={() => handleToggleType(option.value)}
                    />
                    <span style={{ color: formData.goalTypes.includes(option.value) ? option.color : '#6b7280' }}>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="dgt-form-group">
              <label>Recommended Plan *</label>
              <textarea
                value={formData.goalDescription}
                onChange={(e) => setFormData((prev) => ({ ...prev, goalDescription: e.target.value }))}
                placeholder="Describe the recommended development plan..."
                rows={4}
                maxLength={2000}
                required
              />
            </div>
          </div>

          <div className="dgt-modal-footer">
            <button type="button" className="cd-btn cd-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="cd-btn cd-btn-primary"
              disabled={formData.goalTypes.length === 0 || !formData.goalDescription.trim() || isSaving}
            >
              {isSaving ? "Saving..." : goal ? "Update" : "Add Recommendation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * GoalCard - Display a single recommended plan
 */
function GoalCard({ goal, onEdit, onDelete }) {
  const getTypeColor = (type) => {
    const option = GOAL_TYPE_OPTIONS.find((o) => o.value === type);
    return option?.color || '#6b7280';
  };

  return (
    <div className="dgt-goal-card">
      <div className="dgt-goal-header">
        <div className="dgt-goal-type-badges">
          {(goal.goalTypes || []).map((type) => (
            <span
              key={type}
              className="dgt-type-badge"
              style={{ backgroundColor: getTypeColor(type) }}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </span>
          ))}
          {(!goal.goalTypes || goal.goalTypes.length === 0) && goal.goalName && (
            <span className="dgt-legacy-name">{goal.goalName}</span>
          )}
        </div>
        <div className="dgt-goal-actions">
          <button className="dgt-action-btn" onClick={onEdit} title="Edit">
            <IconEdit />
          </button>
          <button className="dgt-action-btn dgt-action-delete" onClick={onDelete} title="Delete">
            <IconTrash />
          </button>
        </div>
      </div>

      {goal.goalDescription && (
        <p className="dgt-goal-description">{goal.goalDescription}</p>
      )}
    </div>
  );
}

/**
 * ResourceCard - Display a single recommended resource
 */
function ResourceCard({ resource, onRemove }) {
  return (
    <div className="dgt-goal-card">
      <div className="dgt-goal-header">
        <div className="dgt-goal-type-badges">
          <span className="dgt-type-badge" style={{ backgroundColor: '#6b7280' }}>
            {resource.resourceType || 'Resource'}
          </span>
        </div>
        <div className="dgt-goal-actions">
          <button className="dgt-action-btn dgt-action-delete" onClick={onRemove} title="Remove">
            <IconTrash />
          </button>
        </div>
      </div>
      <p className="dgt-goal-description">{resource.resourceTitle}</p>
    </div>
  );
}

/**
 * Main DevelopmentGoalsTab Component
 */
export default function DevelopmentGoalsTab({ setId, values }) {
  const [expandedValues, setExpandedValues] = useState({});
  const [expandedCompetencies, setExpandedCompetencies] = useState({});
  const [goalModal, setGoalModal] = useState({ isOpen: false, goal: null, competencyInfo: null });
  const [resourcePicker, setResourcePicker] = useState({ isOpen: false, valueIndex: null, competencyIndex: null });

  // API hooks — plans
  const { data: goals = [], isLoading, refetch } = useGetDevelopmentGoalsQuery(setId);
  const [addGoal, { isLoading: isAdding }] = useAddDevelopmentGoalMutation();
  const [updateGoal, { isLoading: isUpdating }] = useUpdateDevelopmentGoalMutation();
  const [deleteGoal] = useDeleteDevelopmentGoalMutation();

  // API hooks — resources
  const { data: resources = [], refetch: refetchResources } = useGetRecommendedResourcesQuery(setId);
  const [addResource] = useAddRecommendedResourceMutation();
  const [removeResource] = useRemoveRecommendedResourceMutation();

  // Group goals by value and competency
  const goalsByValueCompetency = {};
  goals.forEach((goal) => {
    const key = `${goal.valueIndex}-${goal.competencyIndex}`;
    if (!goalsByValueCompetency[key]) goalsByValueCompetency[key] = [];
    goalsByValueCompetency[key].push(goal);
  });

  // Group resources by value and competency
  const resourcesByValueCompetency = {};
  resources.forEach((resource) => {
    const key = `${resource.valueIndex}-${resource.competencyIndex}`;
    if (!resourcesByValueCompetency[key]) resourcesByValueCompetency[key] = [];
    resourcesByValueCompetency[key].push(resource);
  });

  const toggleValue = (vIdx) => {
    setExpandedValues((prev) => ({ ...prev, [vIdx]: !prev[vIdx] }));
  };

  const toggleCompetency = (vIdx, cIdx) => {
    const key = `${vIdx}-${cIdx}`;
    setExpandedCompetencies((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleOpenAddGoal = (vIdx, cIdx, valueName, competencyName) => {
    setGoalModal({
      isOpen: true,
      goal: null,
      competencyInfo: { valueIndex: vIdx, competencyIndex: cIdx, valueName, competencyName },
    });
  };

  const handleOpenEditGoal = (goal) => {
    setGoalModal({
      isOpen: true,
      goal,
      competencyInfo: {
        valueIndex: goal.valueIndex,
        competencyIndex: goal.competencyIndex,
        valueName: goal.valueName,
        competencyName: goal.competencyName,
      },
    });
  };

  const handleSaveGoal = async (formData) => {
    try {
      if (goalModal.goal) {
        await updateGoal({
          setId,
          goalId: goalModal.goal._id,
          ...formData,
        }).unwrap();
      } else {
        await addGoal({
          setId,
          valueIndex: goalModal.competencyInfo.valueIndex,
          competencyIndex: goalModal.competencyInfo.competencyIndex,
          ...formData,
        }).unwrap();
      }
      refetch();
      setGoalModal({ isOpen: false, goal: null, competencyInfo: null });
    } catch (error) {
      console.error("Failed to save goal:", error);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm("Are you sure you want to delete this recommendation?")) return;
    try {
      await deleteGoal({ setId, goalId }).unwrap();
      refetch();
    } catch (error) {
      console.error("Failed to delete goal:", error);
    }
  };

  const handleAddResource = async (resource) => {
    try {
      await addResource({
        setId,
        valueIndex: resourcePicker.valueIndex,
        competencyIndex: resourcePicker.competencyIndex,
        resourceId: resource._id,
        resourceTitle: resource.title,
        resourceType: resource.type,
        resourceUrl: resource.url || "",
      }).unwrap();
      refetchResources();
      setResourcePicker({ isOpen: false, valueIndex: null, competencyIndex: null });
    } catch (error) {
      console.error("Failed to add resource:", error);
    }
  };

  const handleRemoveResource = async (valueIndex, competencyIndex, resourceIndex) => {
    if (!window.confirm("Remove this recommended resource?")) return;
    try {
      await removeResource({ setId, valueIndex, competencyIndex, resourceIndex }).unwrap();
      refetchResources();
    } catch (error) {
      console.error("Failed to remove resource:", error);
    }
  };

  if (isLoading) {
    return <div className="dgt-loading">Loading...</div>;
  }

  return (
    <div className="dgt-container">
      <div className="dgt-intro">
        <h3>Recommended Development Plans & Resources</h3>
        <p>Define recommended development plans and learning resources for each competency. Users will see these as suggestions when building their own plans.</p>
      </div>

      {values?.map((value, vIdx) => {
        const isValueExpanded = expandedValues[vIdx] !== false;

        return (
          <div key={vIdx} className="dgt-value-section">
            <div className="dgt-value-header" onClick={() => toggleValue(vIdx)}>
              <IconChevron isExpanded={isValueExpanded} />
              <span className="dgt-value-name">{value.valueName}</span>
              <span className="dgt-value-badge">
                {value.competencies?.length || 0} competencies
              </span>
            </div>

            {isValueExpanded && (
              <div className="dgt-value-body">
                {value.competencies?.map((competency, cIdx) => {
                  const compKey = `${vIdx}-${cIdx}`;
                  const isCompExpanded = expandedCompetencies[compKey] !== false;
                  const competencyGoals = goalsByValueCompetency[compKey] || [];
                  const competencyResources = resourcesByValueCompetency[compKey] || [];

                  return (
                    <div key={cIdx} className="dgt-competency-section">
                      <div
                        className="dgt-competency-header"
                        onClick={() => toggleCompetency(vIdx, cIdx)}
                      >
                        <IconChevron isExpanded={isCompExpanded} />
                        <span className="dgt-competency-name">{competency.competencyName}</span>
                        <span className="dgt-competency-badge">
                          {competencyGoals.length} plan{competencyGoals.length !== 1 ? 's' : ''} · {competencyResources.length} resource{competencyResources.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {isCompExpanded && (
                        <div className="dgt-competency-body">
                          {/* Recommended Plans Section */}
                          <div className="dgt-section">
                            <h4 className="dgt-section-label">Recommended Plans</h4>
                            {competencyGoals.length > 0 && (
                              <div className="dgt-goals-grid">
                                {competencyGoals.map((goal) => (
                                  <GoalCard
                                    key={goal._id}
                                    goal={goal}
                                    onEdit={() => handleOpenEditGoal(goal)}
                                    onDelete={() => handleDeleteGoal(goal._id)}
                                  />
                                ))}
                              </div>
                            )}
                            <button
                              className="dgt-add-goal-btn"
                              onClick={() =>
                                handleOpenAddGoal(vIdx, cIdx, value.valueName, competency.competencyName)
                              }
                            >
                              <IconPlus /> Add Recommendation
                            </button>
                          </div>

                          {/* Recommended Resources Section */}
                          <div className="dgt-section">
                            <h4 className="dgt-section-label">Recommended Resources</h4>
                            {competencyResources.length > 0 && (
                              <div className="dgt-goals-grid">
                                {competencyResources.map((resource, rIdx) => (
                                  <ResourceCard
                                    key={resource.resourceId || rIdx}
                                    resource={resource}
                                    onRemove={() => handleRemoveResource(vIdx, cIdx, resource.resourceIndex)}
                                  />
                                ))}
                              </div>
                            )}
                            <button
                              className="dgt-add-goal-btn"
                              onClick={() => setResourcePicker({ isOpen: true, valueIndex: vIdx, competencyIndex: cIdx })}
                            >
                              <IconPlus /> Browse Library
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Goal Form Modal */}
      <GoalFormModal
        isOpen={goalModal.isOpen}
        onClose={() => setGoalModal({ isOpen: false, goal: null, competencyInfo: null })}
        onSave={handleSaveGoal}
        goal={goalModal.goal}
        competencyInfo={goalModal.competencyInfo}
        isSaving={isAdding || isUpdating}
      />

      {/* Learning Picker Modal for Resources */}
      {resourcePicker.isOpen && (
        <LearningPickerModal
          onSelect={handleAddResource}
          onClose={() => setResourcePicker({ isOpen: false, valueIndex: null, competencyIndex: null })}
        />
      )}
    </div>
  );
}
